import { fitsFootprint, roofFootprint, checkRoofRows } from "./siteGeometry";
import { validPolygon } from "./footprint";
import { overlapCount } from "./moduleLayout";

export function panelTurnAllowed(quads, site) {
  if (!quads.length || quads.some(q => [...q.low, ...q.high].some(p =>
    ![p.x, p.y, p.z].every(Number.isFinite)))) return false;
  if (!modulesFitRoof(quads, site?.roof) || overlapCount(quads)) return false;
  const check = checkRoofRows(site, quads);
  return !check.outside && !check.belowRoof && !check.intersecting;
}

export function modulesFitRoof(quads, roof, delta = { x: 0, y: 0 }) {
  if (!roof) return true;
  const ring = roofFootprint(roof);
  if (!validPolygon(ring)) return true;
  return quads.every(q => fitsFootprint([q.low[0], q.low[1], q.high[1], q.high[0]]
    .map(p => ({ x: p.x + delta.x, y: p.y + delta.y })), ring, Math.max(0, Number(roof.setbackM) || 0)));
}

const centre = q => ({ x: (q.low[0].x + q.low[1].x + q.high[0].x + q.high[1].x) / 4,
  y: (q.low[0].y + q.low[1].y + q.high[0].y + q.high[1].y) / 4 });
export const planEditFields = ["row_modules", "field_rows", "excluded_slots", "module_offsets", "module_array",
  "array_offset_east_m", "array_offset_north_m", "module_assignments", "manual_layout_edited"];

export function editModulePlan(form, operation, { rowModules, positions, buildQuads, roof }) {
  const before = { ...form, row_modules: rowModules };
  if (operation.type === "move-array") {
    const visible = buildQuads(before).filter(q => !(form.excluded_slots || []).includes(q.slot));
    if (!visible.length || ![operation.delta?.x, operation.delta?.y].every(Number.isFinite) ||
      Math.hypot(operation.delta.x, operation.delta.y) < 1e-10 ||
      !modulesFitRoof(visible, roof, operation.delta)) return null;
    return { form: { ...before,
      array_offset_east_m: (Number(form.array_offset_east_m) || 0) + operation.delta.x,
      array_offset_north_m: (Number(form.array_offset_north_m) || 0) + operation.delta.y } };
  }
  if (operation.type === "move") {
    if (!buildQuads(before).some(q => q.slot === operation.slot) ||
      (form.excluded_slots || []).includes(operation.slot) ||
      ![operation.delta?.x, operation.delta?.y].every(Number.isFinite) ||
      Math.hypot(operation.delta.x, operation.delta.y) < 1e-10 ||
      !modulesFitRoof(buildQuads(before).filter(q => q.slot === operation.slot), roof, operation.delta)) return null;
    const add = old => ({ ...old,
      eastM: (Number(old?.eastM) || 0) + operation.delta.x,
      northM: (Number(old?.northM) || 0) + operation.delta.y });
    // A move changes geometry only: retain the slot, wiring and unassigned state.
    return { slot: operation.slot, form: form.module_array ? { ...before, module_array: { ...form.module_array,
      individualOffsets: { ...form.module_array.individualOffsets,
        [operation.slot]: add(form.module_array.individualOffsets?.[operation.slot]) } } }
      : { ...before, module_offsets: { ...form.module_offsets, [operation.slot]: add(form.module_offsets?.[operation.slot]) } } };
  }
  if (!["add", "remove", "restore"].includes(operation.type)) return null;
  const next = { ...before, manual_layout_edited: true,
    module_assignments: form.module_assignments ?? positions.map(p => ({ ...p })),
    excluded_slots: [...(form.excluded_slots || [])] };
  if (operation.type === "remove") {
    next.excluded_slots = [...new Set([...next.excluded_slots, ...operation.slots])];
    return { form: next };
  }
  if (operation.type === "restore") {
    next.excluded_slots = next.excluded_slots.filter(slot => slot !== operation.slot);
    return { form: next };
  }
  const tiers = Number(form.row_vertical_modules) || 1;
  const rows = rowModules.length ? [...rowModules] : [0];
  const row = Math.min(rows.length, Math.max(1, operation.row || rows.length));
  const column = Number(rows[row - 1]) || 0;
  const slot = `${row}:${column}:0`;
  rows[row - 1] = column + 1;
  next.row_modules = rows;
  next.field_rows = String(rows.length);
  // Adding one panel is one panel, including on a five-tier original grid.
  for (let t = 1; t < tiers; t++) next.excluded_slots.push(`${row}:${column}:${t}`);
  const old = buildQuads(before), grown = buildQuads(next);
  const anchor = old[0], match = anchor && grown.find(q => q.slot === anchor.slot);
  if (match) {
    const a = centre(anchor), b = centre(match);
    next.array_offset_east_m = (Number(next.array_offset_east_m) || 0) + a.x - b.x;
    next.array_offset_north_m = (Number(next.array_offset_north_m) || 0) + a.y - b.y;
  }
  if (operation.point) {
    const q = buildQuads(next).find(q => q.slot === slot);
    if (q) {
      const c = centre(q), delta = { eastM: operation.point.x - c.x, northM: operation.point.y - c.y };
      if (next.module_array) next.module_array = { ...next.module_array,
        individualOffsets: { ...next.module_array.individualOffsets, [slot]: delta } };
      else next.module_offsets = { ...next.module_offsets, [slot]: delta };
    }
  }
  return { form: next, slot };
}
